/**
 * Thin client for Vercel `/api/*` routes. Safe to import from game code later.
 * Uses same-origin URLs with Vite `base` when set.
 */

import { apiPath } from "./apiPath";

const jsonHeaders = { Accept: "application/json", "Content-Type": "application/json" };

export async function fetchHealth(): Promise<{ ok: boolean; hasDatabase: boolean }> {
  const r = await fetch(apiPath("/api/health"), { credentials: "include", headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`health ${r.status}`);
  return r.json() as Promise<{ ok: boolean; hasDatabase: boolean }>;
}

export type LeaderboardRow = {
  id: string;
  value: number;
  mode: string;
  metric: string;
  at: string;
  guest: boolean;
  displayName: string | null;
  userId?: string;
  device?: string | null;
  hasReplay?: boolean;
};

export async function fetchMyLeaderboardRank(
  mode: string,
  opts: { level?: number; presetId?: string } = {},
): Promise<{ myRank: number | null; myValue: number | null }> {
  const q = new URLSearchParams({ mode, myRank: "1" });
  if (opts.level != null && Number.isFinite(opts.level)) q.set("level", String(Math.floor(opts.level)));
  if (opts.presetId) q.set("presetId", opts.presetId);
  const r = await fetch(`${apiPath("/api/leaderboard")}?${q}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) return { myRank: null, myValue: null };
  const data = (await r.json()) as { myRank?: unknown; myValue?: unknown };
  const mr = data.myRank;
  const mv = data.myValue;
  const myRank =
    mr != null && Number.isFinite(Number(mr)) ? Math.floor(Number(mr)) : null;
  const myValue =
    mv != null && Number.isFinite(Number(mv)) ? Number(mv) : null;
  return { myRank, myValue };
}

export async function fetchLeaderboard(
  mode: string,
  opts: { limit?: number; metric?: string } = {},
): Promise<LeaderboardRow[]> {
  const q = new URLSearchParams({ mode, limit: String(opts.limit ?? 20) });
  const r = await fetch(`${apiPath("/api/leaderboard")}?${q}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`leaderboard get ${r.status}`);
  const data = (await r.json()) as { items: LeaderboardRow[] };
  return data.items ?? [];
}

export async function submitLeaderboardScore(payload: {
  mode: string;
  value: number;
  metric?: string;
  guestKey?: string | null;
  clientVersion?: string;
  device?: "pc" | "mobile";
}): Promise<{ id: string; ok: boolean }> {
  invalidateMyBestsCache();
  const r = await fetch(apiPath("/api/leaderboard"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({
      mode: payload.mode,
      value: payload.value,
      metric: payload.metric ?? "score",
      guestKey: payload.guestKey ?? undefined,
      clientVersion: payload.clientVersion,
      device: payload.device ?? "pc",
    }),
  });
  if (r.status === 503) {
    const err = (await r.json().catch(() => ({}))) as { hint?: string };
    throw new Error(err.hint || "Cloud leaderboard unavailable");
  }
  if (!r.ok) throw new Error(`leaderboard post ${r.status}`);
  return r.json() as Promise<{ id: string; ok: boolean }>;
}

export type SpeedrunLevelRow = {
  levelIndex: number;
  data: unknown;
  displayName?: string | null;
  description?: string | null;
  authorName?: string | null;
  viewCount?: number;
};

export async function fetchSpeedrunLevels(): Promise<SpeedrunLevelRow[]> {
  const r = await fetch(apiPath("/api/speedrun-levels"), { credentials: "include", headers: { Accept: "application/json" } });
  if (!r.ok) return [];
  const data = (await r.json()) as { levels?: SpeedrunLevelRow[] };
  return data.levels ?? [];
}

export async function deleteAdminSpeedrunLevel(levelIndex: number): Promise<void> {
  const r = await fetch(
    `${apiPath("/api/admin-speedrun-level")}?${new URLSearchParams({ levelIndex: String(levelIndex) })}`,
    { method: "DELETE", credentials: "include" },
  );
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete level ${r.status}`);
}

export async function updateAdminSpeedrunLevelMeta(
  levelIndex: number,
  meta: { displayName?: string | null; description?: string | null; authorName?: string | null },
): Promise<void> {
  const r = await fetch(apiPath("/api/admin-speedrun-level"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ levelIndex, ...meta }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`update level meta ${r.status}`);
}

export async function incrementLevelViewCount(
  kind: "speedrun" | "penalty" | "practice",
  levelIndex: number,
): Promise<number | null> {
  try {
    const r = await fetch(apiPath("/api/level-views"), {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders,
      body: JSON.stringify({ kind, levelIndex }),
    });
    if (!r.ok) return null;
    const data = (await r.json()) as { viewCount?: number | null };
    return data.viewCount == null ? null : Math.floor(Number(data.viewCount) || 0);
  } catch {
    return null;
  }
}

export type PenaltyLevelRow = {
  levelIndex: number;
  data: unknown;
  displayName?: string | null;
  description?: string | null;
  authorName?: string | null;
  viewCount?: number;
};

export async function fetchPenaltyLevels(): Promise<PenaltyLevelRow[]> {
  const r = await fetch(apiPath("/api/penalty-levels"), { credentials: "include", headers: { Accept: "application/json" } });
  if (!r.ok) return [];
  const data = (await r.json()) as { levels?: PenaltyLevelRow[] };
  return data.levels ?? [];
}

export async function deleteAdminPenaltyLevel(levelIndex: number): Promise<void> {
  const r = await fetch(
    `${apiPath("/api/admin-penalty-level")}?${new URLSearchParams({ levelIndex: String(levelIndex) })}`,
    { method: "DELETE", credentials: "include" },
  );
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete penalty level ${r.status}`);
}

export async function updateAdminPenaltyLevelMeta(
  levelIndex: number,
  meta: { displayName?: string | null; description?: string | null; authorName?: string | null },
): Promise<void> {
  const r = await fetch(apiPath("/api/admin-penalty-level"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ levelIndex, ...meta }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`update penalty level meta ${r.status}`);
}

export async function saveAdminPenaltyLevel(
  levelIndex: number,
  data: object,
  displayName?: string | null,
): Promise<void> {
  const body: Record<string, unknown> = { levelIndex, data };
  if (displayName !== undefined) body.displayName = displayName;
  const r = await fetch(apiPath("/api/admin-penalty-level"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (r.status === 403) {
    const err = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || "forbidden");
  }
  if (!r.ok) throw new Error(`save penalty level ${r.status}`);
}

export type PenaltyEditorDraftSummary = {
  id: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  authorDisplayName: string | null;
  authorUserId: string;
};

export type PenaltyEditorDraftRow = PenaltyEditorDraftSummary & { data: unknown };

export async function fetchPenaltyEditorDrafts(): Promise<PenaltyEditorDraftSummary[]> {
  const r = await fetch(apiPath("/api/admin-penalty-drafts"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`penalty drafts list ${r.status}`);
  const data = (await r.json()) as { drafts?: PenaltyEditorDraftSummary[] };
  return data.drafts ?? [];
}

export async function fetchPenaltyEditorDraft(id: string): Promise<PenaltyEditorDraftRow> {
  const r = await fetch(`${apiPath("/api/admin-penalty-drafts")}?${new URLSearchParams({ id })}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (r.status === 404) throw new Error("not_found");
  if (!r.ok) throw new Error(`penalty draft get ${r.status}`);
  const data = (await r.json()) as { draft: PenaltyEditorDraftRow };
  return data.draft;
}

export async function savePenaltyEditorDraft(
  data: object,
  name?: string | null,
): Promise<PenaltyEditorDraftSummary> {
  const r = await fetch(apiPath("/api/admin-penalty-drafts"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ data, name: name ?? undefined }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`penalty draft save ${r.status}`);
  const out = (await r.json()) as { draft: PenaltyEditorDraftSummary };
  return out.draft;
}

export async function overwritePenaltyEditorDraft(
  id: string,
  data: object,
  name?: string | null,
): Promise<PenaltyEditorDraftSummary> {
  const body: Record<string, unknown> = { data };
  if (name !== undefined) body.name = name;
  const r = await fetch(`${apiPath("/api/admin-penalty-drafts")}?${new URLSearchParams({ id })}`, {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`penalty draft overwrite ${r.status}`);
  const out = (await r.json()) as { draft: PenaltyEditorDraftSummary };
  return out.draft;
}

export type AdminLeaderboardRowApi = {
  id: string;
  userId: string | null;
  displayName: string;
  gameMode: string;
  metric: string;
  speedrunLevel: number | null;
  value: number;
  device: string | null;
  createdAt: string;
  totalDeaths?: number | null;
  totalPlayTimeSec?: number | null;
};

export async function fetchAdminLeaderboardEntries(q: {
  search?: string;
  mode?: string;
  speedrunLevel?: number | null;
}): Promise<AdminLeaderboardRowApi[]> {
  const p = new URLSearchParams();
  if (q.search) p.set("search", q.search);
  if (q.mode) p.set("mode", q.mode);
  if (q.speedrunLevel != null && Number.isFinite(q.speedrunLevel)) p.set("speedrunLevel", String(q.speedrunLevel));
  const r = await fetch(`${apiPath("/api/admin-leaderboard-entries")}?${p}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin entries ${r.status}`);
  const data = (await r.json()) as { items: AdminLeaderboardRowApi[] };
  return data.items ?? [];
}

export async function deleteAdminLeaderboardEntry(id: string): Promise<{ userId: string | null; gameMode: string | null }> {
  const r = await fetch(`${apiPath("/api/admin-leaderboard-entries")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete ${r.status}`);
  const data = (await r.json()) as { userId?: string | null; gameMode?: string | null };
  return { userId: data.userId ?? null, gameMode: data.gameMode ?? null };
}

export async function deleteAllAdminLeaderboardEntries(
  userId: string,
  mode: string,
  speedrunLevel?: number,
): Promise<{ userId: string; gameMode: string }> {
  const p = new URLSearchParams({ userId, mode, all: "1" });
  if (speedrunLevel != null && Number.isInteger(speedrunLevel) && speedrunLevel >= 1) {
    p.set("speedrunLevel", String(speedrunLevel));
  }
  const r = await fetch(`${apiPath("/api/admin-leaderboard-entries")}?${p}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete all ${r.status}`);
  const data = (await r.json()) as { userId?: string; gameMode?: string };
  return { userId: data.userId ?? userId, gameMode: data.gameMode ?? mode };
}

export async function deleteAdminUserStats(userId: string): Promise<{ userId: string }> {
  const p = new URLSearchParams({ userId });
  const r = await fetch(`${apiPath("/api/admin-user-stats")}?${p}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete stats ${r.status}`);
  const data = (await r.json()) as { userId?: string };
  return { userId: data.userId ?? userId };
}

export type AdminPlayerStatsDetailApi = {
  userId: string;
  displayName: string | null;
  email: string | null;
  totalDeaths: number;
  totalPlayTimeSec: number;
  dotsBalance: number;
  purchases: Array<{
    cosmeticId: string;
    name: string;
    category: string;
    pricePaid: number;
    unlockType: string;
    purchasedAt: string;
    ownedCustomColors: string[];
    activeCustomColor: string | null;
    equipped: boolean;
  }>;
  achievements: { id: string; title: string }[];
  syncedRecords: Array<{
    kind: "best" | "speedrun" | "sandboxSpeedrun";
    key: string;
    label: string;
    value: number;
  }>;
  leaderboardRecords: Array<{
    id: string;
    gameMode: string;
    metric: string;
    speedrunLevel: number | null;
    value: number;
    device: string | null;
    createdAt: string;
  }>;
};

export type AdminPlayerStatsLookup =
  | { kind: "found"; detail: AdminPlayerStatsDetailApi }
  | { kind: "ambiguous"; candidates: Array<{ userId: string; displayName: string | null; email: string | null }> }
  | { kind: "not_found" };

export async function lookupAdminPlayerStats(search: string): Promise<AdminPlayerStatsLookup> {
  const p = new URLSearchParams({ search });
  const r = await fetch(`${apiPath("/api/admin-user-stats")}?${p}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (r.status === 404) return { kind: "not_found" };
  if (!r.ok) throw new Error(`admin player stats ${r.status}`);
  const data = (await r.json()) as AdminPlayerStatsDetailApi & {
    ambiguous?: boolean;
    candidates?: Array<{ userId: string; displayName: string | null; email: string | null }>;
  };
  if (data.ambiguous && data.candidates?.length) {
    return { kind: "ambiguous", candidates: data.candidates };
  }
  return { kind: "found", detail: data };
}

export async function fetchAdminPlayerStats(userId: string): Promise<AdminPlayerStatsDetailApi> {
  const p = new URLSearchParams({ userId });
  const r = await fetch(`${apiPath("/api/admin-user-stats")}?${p}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (r.status === 404) throw new Error("not_found");
  if (!r.ok) throw new Error(`admin player stats ${r.status}`);
  return (await r.json()) as AdminPlayerStatsDetailApi;
}

export async function patchAdminPlayerStats(
  userId: string,
  patch: { totalDeaths?: number; totalPlayTimeSec?: number },
): Promise<{ userId: string }> {
  const r = await fetch(apiPath("/api/admin-user-stats"), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ userId, ...patch }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`patch stats ${r.status}`);
  const data = (await r.json()) as { userId?: string };
  return { userId: data.userId ?? userId };
}

export async function patchAdminPlayerDots(
  userId: string,
  mode: "set" | "add" | "subtract",
  amount: number,
): Promise<{ userId: string; balance: number }> {
  const r = await fetch(apiPath("/api/admin-user-stats"), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ userId, dotsMode: mode, dotsAmount: amount }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`patch dots ${r.status}`);
  const data = (await r.json()) as { userId?: string; balance?: number };
  return { userId: data.userId ?? userId, balance: Math.max(0, Math.floor(Number(data.balance) || 0)) };
}

export async function removeAdminPlayerPurchase(
  userId: string,
  cosmeticId: string,
  refund: boolean,
): Promise<{ userId: string; refunded: number; balance: number }> {
  const r = await fetch(apiPath("/api/admin-user-stats"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "remove_purchase", userId, cosmeticId, refund }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`remove purchase ${r.status}`);
  const data = (await r.json()) as { userId?: string; refunded?: number; balance?: number };
  return {
    userId: data.userId ?? userId,
    refunded: Math.max(0, Math.floor(Number(data.refunded) || 0)),
    balance: Math.max(0, Math.floor(Number(data.balance) || 0)),
  };
}

export async function deleteAdminPlayerStat(
  userId: string,
  stat: "playtime" | "deaths",
): Promise<{ userId: string }> {
  const p = new URLSearchParams({ userId, stat });
  const r = await fetch(`${apiPath("/api/admin-user-stats")}?${p}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete stat ${r.status}`);
  const data = (await r.json()) as { userId?: string };
  return { userId: data.userId ?? userId };
}

export async function deleteAdminPlayerAchievement(
  userId: string,
  achievementId: string,
): Promise<{ userId: string }> {
  const p = new URLSearchParams({ userId, achievement: achievementId });
  const r = await fetch(`${apiPath("/api/admin-user-stats")}?${p}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete achievement ${r.status}`);
  const data = (await r.json()) as { userId?: string };
  return { userId: data.userId ?? userId };
}

export async function deleteAdminPlayerSyncedRecord(
  userId: string,
  syncKind: "best" | "speedrun" | "sandboxSpeedrun",
  syncKey: string,
): Promise<{ userId: string }> {
  const p = new URLSearchParams({ userId, syncKind, syncKey });
  const r = await fetch(`${apiPath("/api/admin-user-stats")}?${p}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete synced record ${r.status}`);
  const data = (await r.json()) as { userId?: string };
  return { userId: data.userId ?? userId };
}

export type AdminTimeoutUser = {
  id: string;
  displayName: string | null;
  email: string | null;
  timedOut: boolean;
  timedOutUntil: string | null;
  timedOutAt: string | null;
};

export async function fetchAdminTimeoutUsers(search: string): Promise<AdminTimeoutUser[]> {
  const p = new URLSearchParams({ search });
  const r = await fetch(`${apiPath("/api/admin-user-timeout")}?${p}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin timeout search ${r.status}`);
  const data = (await r.json()) as { items: AdminTimeoutUser[] };
  return data.items ?? [];
}

export async function fetchAdminActiveTimeoutUsers(): Promise<AdminTimeoutUser[]> {
  const r = await fetch(`${apiPath("/api/admin-user-timeout")}?active=1`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin active timeouts ${r.status}`);
  const data = (await r.json()) as { items: AdminTimeoutUser[] };
  return data.items ?? [];
}

export type AdminTimeoutAction = "apply" | "extend" | "reduce";

export async function applyAdminUserTimeout(
  userId: string,
  duration: string,
  action: AdminTimeoutAction = "apply",
): Promise<AdminTimeoutUser> {
  const r = await fetch(apiPath("/api/admin-user-timeout"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ userId, duration, action }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`apply timeout ${r.status}`);
  const data = (await r.json()) as { user: AdminTimeoutUser };
  return data.user;
}

export async function clearAdminUserTimeout(userId: string): Promise<AdminTimeoutUser> {
  const p = new URLSearchParams({ userId });
  const r = await fetch(`${apiPath("/api/admin-user-timeout")}?${p}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`clear timeout ${r.status}`);
  const data = (await r.json()) as { user: AdminTimeoutUser };
  return data.user;
}

export type AdminPasswordRecoveryUser = {
  id: string;
  displayName: string | null;
  email: string | null;
  pendingPasswordReset: boolean;
};

export async function fetchAdminPasswordRecoveryUsers(search: string): Promise<AdminPasswordRecoveryUser[]> {
  const p = new URLSearchParams({ search });
  const r = await fetch(`${apiPath("/api/admin-password-recovery")}?${p}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin password recovery ${r.status}`);
  const data = (await r.json()) as { items: AdminPasswordRecoveryUser[] };
  return data.items ?? [];
}

export async function createAdminTempPassword(userId: string): Promise<{
  displayName: string | null;
  email: string | null;
  tempPassword: string;
}> {
  const r = await fetch(apiPath("/api/admin-password-recovery"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ userId }),
  });
  if (r.status === 403) throw new Error("forbidden");
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || `create temp password ${r.status}`);
  return data as { displayName: string | null; email: string | null; tempPassword: string };
}

export type AdminPermissionKey =
  | "records"
  | "passwordRecovery"
  | "speedrunLevels"
  | "speedrunEditor"
  | "speedrunPublish"
  | "speedrunOverwrite"
  | "penaltyLevels"
  | "penaltyEditor"
  | "penaltyPublish"
  | "penaltyOverwrite"
  | "practiceLevels"
  | "practiceEditor"
  | "practicePublish"
  | "practiceOverwrite"
  | "administrators"
  | "achievements"
  | "wallEdit"
  | "wallPublish"
  | "wallDelete"
  | "shopCreator"
  | "shopItems"
  | "shopOverwrite"
  | "shopDelete"
  | "shopDots"
  | "crates"
  | "crateCreator"
  | "cratesPublish"
  | "cratesOverwrite"
  | "splashEdit"
  | "challenges"
  | "campaign"
  | "tasksEditor"
  | "battlePassEditor"
  | "announcementsSend"
  | "whatsNew"
  | "themeEditor"
  | "tournamentsEditor"
  | "marathon"
  | "iconEditor";

export type AdminGrantUser = {
  id: string;
  displayName: string | null;
  email: string | null;
  permissions: AdminPermissionKey[];
};

export async function fetchAdminGrantUsers(search?: string): Promise<AdminGrantUser[]> {
  const p = new URLSearchParams();
  if (search && search.trim().length >= 2) p.set("search", search.trim());
  const r = await fetch(`${apiPath("/api/admin-administrators")}?${p}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin administrators ${r.status}`);
  const data = (await r.json()) as { items: AdminGrantUser[] };
  return data.items ?? [];
}

export type AccountSettingsInfo = {
  hasPasswordAccount: boolean;
  displayName: string | null;
  email: string | null;
  canChangeUsername: boolean;
  usernameChangeAvailableAt: string | null;
};

export async function fetchAccountSettings(): Promise<AccountSettingsInfo | null> {
  const r = await fetch(apiPath("/api/account-settings"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 401 || r.status === 503) return null;
  if (!r.ok) throw new Error(`account settings ${r.status}`);
  return r.json() as Promise<AccountSettingsInfo>;
}

export async function changeAccountPassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean; message?: string }> {
  const r = await fetch(apiPath("/api/account-settings"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "changePassword", currentPassword, newPassword }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || `change password ${r.status}`);
  return data as { ok: boolean; message?: string };
}

export async function changeAccountUsername(
  newUsername: string,
): Promise<{ ok: boolean; displayName: string; canChangeUsername: boolean; usernameChangeAvailableAt: string | null }> {
  const r = await fetch(apiPath("/api/account-settings"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "changeUsername", newUsername }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || `change username ${r.status}`);
  return data as {
    ok: boolean;
    displayName: string;
    canChangeUsername: boolean;
    usernameChangeAvailableAt: string | null;
  };
}

export async function patchAdminUserPermissions(
  userId: string,
  permissions: AdminPermissionKey[],
): Promise<AdminGrantUser> {
  const r = await fetch(apiPath("/api/admin-administrators"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ userId, permissions }),
  });
  if (r.status === 403) throw new Error("forbidden");
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || `patch admin permissions ${r.status}`);
  return {
    id: data.userId ?? userId,
    displayName: null,
    email: null,
    permissions: (data.permissions ?? permissions) as AdminPermissionKey[],
  };
}

export async function saveAdminSpeedrunLevel(
  levelIndex: number,
  data: object,
  displayName?: string | null,
): Promise<void> {
  const body: Record<string, unknown> = { levelIndex, data };
  if (displayName !== undefined) body.displayName = displayName;
  const r = await fetch(apiPath("/api/admin-speedrun-level"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (r.status === 403) {
    const err = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || "forbidden");
  }
  if (!r.ok) throw new Error(`save level ${r.status}`);
}

export type SpeedrunEditorDraftSummary = {
  id: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  authorDisplayName: string | null;
  authorUserId: string;
};

export type SpeedrunEditorDraftRow = SpeedrunEditorDraftSummary & { data: unknown };

export async function fetchSpeedrunEditorDrafts(): Promise<SpeedrunEditorDraftSummary[]> {
  const r = await fetch(apiPath("/api/admin-speedrun-drafts"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`drafts list ${r.status}`);
  const data = (await r.json()) as { drafts?: SpeedrunEditorDraftSummary[] };
  return data.drafts ?? [];
}

export async function fetchSpeedrunEditorDraft(id: string): Promise<SpeedrunEditorDraftRow> {
  const r = await fetch(`${apiPath("/api/admin-speedrun-drafts")}?${new URLSearchParams({ id })}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (r.status === 404) throw new Error("not_found");
  if (!r.ok) throw new Error(`draft get ${r.status}`);
  const data = (await r.json()) as { draft: SpeedrunEditorDraftRow };
  return data.draft;
}

export async function saveSpeedrunEditorDraft(
  data: object,
  name?: string | null,
): Promise<SpeedrunEditorDraftSummary> {
  const r = await fetch(apiPath("/api/admin-speedrun-drafts"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ data, name: name ?? undefined }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`draft save ${r.status}`);
  const out = (await r.json()) as { draft: SpeedrunEditorDraftSummary };
  return out.draft;
}

export async function overwriteSpeedrunEditorDraft(
  id: string,
  data: object,
  name?: string | null,
): Promise<SpeedrunEditorDraftSummary> {
  const body: Record<string, unknown> = { data };
  if (name !== undefined) body.name = name;
  const r = await fetch(`${apiPath("/api/admin-speedrun-drafts")}?${new URLSearchParams({ id })}`, {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`draft overwrite ${r.status}`);
  const out = (await r.json()) as { draft: SpeedrunEditorDraftSummary };
  return out.draft;
}

// ---- Practice Levels ----

export type PracticeLevelRow = {
  levelIndex: number;
  data: unknown;
  displayName?: string | null;
  description?: string | null;
  category?: string | null;
  authorName?: string | null;
  viewCount?: number;
};

export async function fetchPracticeLevels(): Promise<PracticeLevelRow[]> {
  const r = await fetch(apiPath("/api/practice-levels"), { credentials: "include", headers: { Accept: "application/json" } });
  if (!r.ok) return [];
  const data = (await r.json()) as { levels?: PracticeLevelRow[] };
  return data.levels ?? [];
}

export async function fetchPracticeHubConfig(): Promise<{ levels: PracticeLevelRow[]; showInCategories: boolean }> {
  const r = await fetch(apiPath("/api/practice-levels"), { credentials: "include", headers: { Accept: "application/json" } });
  if (!r.ok) return { levels: [], showInCategories: false };
  const data = (await r.json()) as { levels?: PracticeLevelRow[]; showInCategories?: boolean };
  return {
    levels: data.levels ?? [],
    showInCategories: !!data.showInCategories,
  };
}

export async function setPracticeShowInCategories(showInCategories: boolean): Promise<boolean> {
  const r = await fetch(apiPath("/api/admin-practice-hub"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ showInCategories }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`practice hub ${r.status}`);
  const data = (await r.json()) as { showInCategories?: boolean };
  return !!data.showInCategories;
}

export async function deleteAdminPracticeLevel(levelIndex: number): Promise<void> {
  const r = await fetch(
    `${apiPath("/api/admin-practice-level")}?${new URLSearchParams({ levelIndex: String(levelIndex) })}`,
    { method: "DELETE", credentials: "include" },
  );
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete practice level ${r.status}`);
}

export async function updateAdminPracticeLevelMeta(
  levelIndex: number,
  meta: { displayName?: string | null; description?: string | null; category?: string | null; authorName?: string | null },
): Promise<void> {
  const r = await fetch(apiPath("/api/admin-practice-level"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ levelIndex, ...meta }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`update practice level meta ${r.status}`);
}

export async function saveAdminPracticeLevel(
  levelIndex: number,
  data: object,
  meta?: { displayName?: string | null; description?: string | null; category?: string | null; authorName?: string | null },
): Promise<void> {
  const body: Record<string, unknown> = { levelIndex, data, ...meta };
  const r = await fetch(apiPath("/api/admin-practice-level"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (r.status === 403) {
    const err = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || "forbidden");
  }
  if (!r.ok) throw new Error(`save practice level ${r.status}`);
}

export type PracticeEditorDraftSummary = {
  id: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  authorDisplayName: string | null;
  authorUserId: string;
};

export type PracticeEditorDraftRow = PracticeEditorDraftSummary & { data: unknown };

export async function fetchPracticeEditorDrafts(): Promise<PracticeEditorDraftSummary[]> {
  const r = await fetch(apiPath("/api/admin-practice-drafts"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`practice drafts list ${r.status}`);
  const data = (await r.json()) as { drafts?: PracticeEditorDraftSummary[] };
  return data.drafts ?? [];
}

export async function fetchPracticeEditorDraft(id: string): Promise<PracticeEditorDraftRow> {
  const r = await fetch(`${apiPath("/api/admin-practice-drafts")}?${new URLSearchParams({ id })}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (r.status === 404) throw new Error("not_found");
  if (!r.ok) throw new Error(`practice draft get ${r.status}`);
  const data = (await r.json()) as { draft: PracticeEditorDraftRow };
  return data.draft;
}

export async function savePracticeEditorDraft(
  data: object,
  name?: string | null,
): Promise<PracticeEditorDraftSummary> {
  const r = await fetch(apiPath("/api/admin-practice-drafts"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ data, name: name ?? undefined }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`practice draft save ${r.status}`);
  const out = (await r.json()) as { draft: PracticeEditorDraftSummary };
  return out.draft;
}

export async function overwritePracticeEditorDraft(
  id: string,
  data: object,
  name?: string | null,
): Promise<PracticeEditorDraftSummary> {
  const body: Record<string, unknown> = { data };
  if (name !== undefined) body.name = name;
  const r = await fetch(`${apiPath("/api/admin-practice-drafts")}?${new URLSearchParams({ id })}`, {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`practice draft overwrite ${r.status}`);
  const out = (await r.json()) as { draft: PracticeEditorDraftSummary };
  return out.draft;
}

export type WallApiResponse = {
  blocks: unknown[];
  live: unknown;
  publishedAt: string | null;
  database?: boolean;
};

export async function fetchWall(): Promise<WallApiResponse> {
  const r = await fetch(apiPath("/api/wall"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`wall ${r.status}`);
  return r.json() as Promise<WallApiResponse>;
}

export type WallAdminResponse = {
  draftBlocks: unknown[];
  publishedBlocks: unknown[];
  draftUpdatedAt: string;
  publishedAt: string | null;
  history: Array<{ id: string; publishedAt: string; label: string | null }>;
  permissions: { edit: boolean; publish: boolean; delete: boolean };
};

export async function fetchWallAdmin(): Promise<WallAdminResponse> {
  const r = await fetch(apiPath("/api/wall-admin"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`wall admin ${r.status}`);
  return r.json() as Promise<WallAdminResponse>;
}

export async function saveWallDraft(blocks: unknown[]): Promise<{ draftUpdatedAt: string }> {
  const r = await fetch(apiPath("/api/wall-admin"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ blocks }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`wall save ${r.status}`);
  return r.json() as Promise<{ draftUpdatedAt: string }>;
}

export async function publishWallDraft(label?: string): Promise<{ publishedAt: string }> {
  const r = await fetch(apiPath("/api/wall-admin"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "publish", label }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`wall publish ${r.status}`);
  return r.json() as Promise<{ publishedAt: string }>;
}

export async function restoreWallRevision(revisionId: string): Promise<{ draftUpdatedAt: string; blockCount: number }> {
  const r = await fetch(apiPath("/api/wall-admin"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "restore", revisionId }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`wall restore ${r.status}`);
  return r.json() as Promise<{ draftUpdatedAt: string; blockCount: number }>;
}

export async function deleteWallRevision(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/wall-admin")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`wall delete revision ${r.status}`);
}

export async function postWallAction(payload: {
  action: "counter" | "poll";
  blockId: string;
  optionIndex?: number;
  cooldownSec?: number;
}): Promise<{ ok: boolean; value?: number }> {
  const r = await fetch(apiPath("/api/wall-action"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `wall action ${r.status}`);
  return data as { ok: boolean; value?: number };
}

export async function uploadWallImage(mimeType: string, dataUrl: string): Promise<{ id: string }> {
  const r = await fetch(apiPath("/api/wall-image"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ mimeType, dataBase64: dataUrl }),
  });
  if (r.status === 403) throw new Error("forbidden");
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `wall image ${r.status}`);
  return data as { id: string };
}

export async function fetchModePlayCounts(opts?: {
  window?: "24h";
}): Promise<{ counts: Record<string, number>; window?: string }> {
  const q = opts?.window === "24h" ? "?window=24h" : "";
  const r = await fetch(apiPath(`/api/mode-plays${q}`), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`mode-plays get ${r.status}`);
  const data = (await r.json()) as { counts?: Record<string, unknown>; window?: string };
  const counts: Record<string, number> = {};
  for (const [k, v] of Object.entries(data.counts || {})) {
    const n = Math.floor(Number(v) || 0);
    if (n >= 0) counts[k] = n;
  }
  return { counts, window: data.window };
}

export async function incrementModePlayCount(modeId: string): Promise<number | null> {
  const r = await fetch(apiPath("/api/mode-plays"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ modeId }),
  });
  if (!r.ok) return null;
  const data = (await r.json()) as { count?: unknown };
  const n = Math.floor(Number(data.count));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export type ShopUnlockType = "dots" | "free" | "achievement" | "challenge" | "score";

export type CosmeticCategory =
  | "hat"
  | "face"
  | "cursor"
  | "cursor_color"
  | "trail"
  | "banner"
  | "theme";

export type TrailMode = "multiColor" | "particle";

export type ThemeCosmeticChannelKey = "background" | "balls" | "buttons" | "text";

export type ThemeCosmeticEquipSlot = ThemeCosmeticChannelKey | "particles";

export type ThemeCosmeticChannel = {
  mode: "solid" | "gradient";
  colors: string[];
  angleDeg?: number;
};

export type ThemeCosmeticData = Partial<Record<ThemeCosmeticChannelKey, ThemeCosmeticChannel>> & {
  decorations?: unknown[];
};

export type ShopCosmeticDto = {
  id: string;
  name: string;
  imageData: string;
  outlineMaskData?: string | null;
  animationFrames?: string[] | null;
  category?: CosmeticCategory;
  trailMode?: TrailMode | null;
  trailColors?: string[] | null;
  themeData?: ThemeCosmeticData | null;
  allowCustomColor?: boolean;
  allowRepurchase?: boolean;
  matchCursorColor?: boolean;
  cursorColorShade?: number;
  matchCursorMaskData?: string | null;
  matchCursorMaskFrames?: string[] | null;
  unlockType: ShopUnlockType;
  priceDots: number | null;
  unlockAchievementId: string | null;
  unlockChallengeId: string | null;
  unlockScoreMode: string | null;
  unlockScoreValue: number | null;
  sortOrder: number;
  unlockLabel?: string;
  crateOnly?: boolean;
  sentOnly?: boolean;
  campaignOnly?: boolean;
  unlockCampaignNodeId?: string | null;
  campaignUnlockLabel?: string | null;
  taskOnly?: boolean;
  unlockTaskId?: string | null;
  marathonOnly?: boolean;
  unlockMarathonSegmentId?: string | null;
  battlePassOnly?: boolean;
  unlockBattlePassLevelId?: string | null;
  limitedItem?: boolean;
  limitedDurationText?: string | null;
  limitedEndsAt?: string | null;
  newCursor?: boolean;
  /** ISO publish/create time (catalog). */
  createdAt?: string | null;
  /** Admin list: current owners (purchase rows). */
  recipientCount?: number;
  /** @deprecated prefer ownedCustomColors / activeCustomColor */
  customColor?: string | null;
  ownedCustomColors?: string[];
  activeCustomColor?: string | null;
};

export type ShopCrateContentDto = {
  cosmeticId: string;
  cosmeticName: string;
  cosmeticCategory: CosmeticCategory;
  cosmeticImageData: string;
  cosmeticOutlineMaskData: string | null;
  cosmeticAnimationFrames?: string[] | null;
  cosmeticMatchCursorColor?: boolean;
  cosmeticCursorColorShade?: number;
  cosmeticMatchCursorMaskData?: string | null;
  cosmeticMatchCursorMaskFrames?: string[] | null;
  cosmeticTrailMode?: TrailMode | null;
  cosmeticTrailColors?: string[] | null;
  cosmeticNewCursor?: boolean;
  probabilityPercent: number;
};

export type ShopCrateDto = {
  id: string;
  name: string;
  imageData: string;
  unlockType: ShopUnlockType;
  priceDots: number | null;
  unlockAchievementId: string | null;
  unlockChallengeId: string | null;
  unlockScoreMode: string | null;
  unlockScoreValue: number | null;
  sortOrder: number;
  unlockLabel?: string;
  contents: ShopCrateContentDto[];
  claimed?: boolean;
  /** Free Task rolls available for this crate (signed-in). */
  freeRolls?: number;
};

export type ModeDotsConfigDto = {
  modeId: string;
  dotsConversionRate: number;
  dotsBonusBase: number;
};

export type CosmeticOutfitDto = {
  id: string;
  name: string;
  equippedCosmeticIds: string[];
  cursorColor?: string;
  trailColor?: string;
};

export type ShopDotsState = {
  balance: number;
  dailyStreak: number;
  ownedCosmeticIds: string[];
  claimedCrateIds?: string[];
  equippedCosmeticIds?: string[];
  equippedCosmeticId?: string | null;
  equippedThemeChannels?: Partial<Record<ThemeCosmeticEquipSlot, string>>;
  dailyLoginReward?: number;
  lastLoginDate?: string | null;
  outfits?: CosmeticOutfitDto[];
};

export async function fetchShopCatalog(): Promise<{
  items: ShopCosmeticDto[];
  crates: ShopCrateDto[];
  modeDotsConfig: ModeDotsConfigDto[];
  dots: ShopDotsState | null;
  freeCrateRolls: Record<string, number>;
}> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 12_000);
  try {
    const [catR, meR] = await Promise.all([
      fetch(apiPath("/api/shop-catalog"), {
        credentials: "include",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      }),
      fetch(apiPath("/api/shop-me"), {
        credentials: "include",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      }),
    ]);
    if (!catR.ok) throw new Error(`shop-catalog ${catR.status}`);
    const catalog = (await catR.json()) as {
      items?: ShopCosmeticDto[];
      crates?: ShopCrateDto[];
      modeDotsConfig?: ModeDotsConfigDto[];
    };
    let dots: ShopDotsState | null = null;
    let purchaseColors: Record<
      string,
      { ownedCustomColors: string[]; activeCustomColor: string | null }
    > = {};
    let claimed = new Set<string>();
    let freeCrateRolls: Record<string, number> = {};
    if (meR.ok) {
      const me = (await meR.json()) as {
        dots?: ShopDotsState | null;
        purchaseColors?: Record<
          string,
          { ownedCustomColors: string[]; activeCustomColor: string | null }
        >;
        claimedCrateIds?: string[];
        freeCrateRolls?: Record<string, number>;
      };
      dots = me.dots ?? null;
      purchaseColors = me.purchaseColors ?? {};
      claimed = new Set(me.claimedCrateIds ?? dots?.claimedCrateIds ?? []);
      if (me.freeCrateRolls && typeof me.freeCrateRolls === "object") {
        for (const [k, v] of Object.entries(me.freeCrateRolls)) {
          const n = Math.floor(Number(v) || 0);
          if (k && n > 0) freeCrateRolls[k] = n;
        }
      }
    }
    const items = (catalog.items ?? []).map((item) => {
      const colors = purchaseColors[item.id];
      return {
        ...item,
        ownedCustomColors: colors?.ownedCustomColors ?? item.ownedCustomColors ?? [],
        activeCustomColor: colors?.activeCustomColor ?? item.activeCustomColor ?? null,
      };
    });
    const byId = new Map(items.map((i) => [i.id, i]));
    const crates = (catalog.crates ?? []).map((crate) => ({
      ...crate,
      claimed: claimed.has(crate.id),
      freeRolls: Math.max(0, Math.floor(Number(freeCrateRolls[crate.id]) || 0)),
      contents: (crate.contents ?? []).map((c) => {
        const src = byId.get(c.cosmeticId);
        if (!src) return c;
        return {
          ...c,
          cosmeticName: src.name || c.cosmeticName,
          cosmeticCategory: (src.category || c.cosmeticCategory) as CosmeticCategory,
          cosmeticImageData: src.imageData,
          cosmeticOutlineMaskData: src.outlineMaskData ?? null,
          cosmeticAnimationFrames: src.animationFrames ?? null,
          cosmeticMatchCursorColor: !!src.matchCursorColor,
          cosmeticCursorColorShade: src.cursorColorShade ?? 0,
          cosmeticMatchCursorMaskData: src.matchCursorMaskData ?? null,
          cosmeticMatchCursorMaskFrames: src.matchCursorMaskFrames ?? null,
          cosmeticTrailMode: src.trailMode ?? null,
          cosmeticTrailColors: src.trailColors ?? null,
        };
      }),
    }));
    return {
      items,
      crates,
      modeDotsConfig: catalog.modeDotsConfig ?? [],
      dots,
      freeCrateRolls,
    };
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") throw new Error("Shop request timed out");
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

/** UTC daily login Dots — call once after signed-in bootstrap (not on GET catalog). */
export async function claimDailyLoginDots(): Promise<ShopDotsState | null> {
  try {
    const r = await fetch(apiPath("/api/dots"), {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders,
      body: JSON.stringify({ action: "claim-daily" }),
    });
    if (!r.ok) return null;
    const data = (await r.json()) as { dots?: ShopDotsState };
    return data.dots ?? null;
  } catch {
    return null;
  }
}

type MyBestsMap = Record<string, { rank: number | null; value: number | null }>;

/**
 * Short-lived client cache for GET /api/my-bests. Game start, Stats and the HUD all ask for
 * the same data back-to-back; without this every run start cost one function invocation
 * (plus 22 DB rank queries). Invalidated on score submit and on account change.
 */
const MY_BESTS_TTL_MS = 30_000;
let myBestsCache: { at: number; data: MyBestsMap } | null = null;
let myBestsInflight: Promise<MyBestsMap> | null = null;
let myBestsGeneration = 0;

/** Drop cached bests (call after a leaderboard POST or when the signed-in user changes). */
export function invalidateMyBestsCache(): void {
  myBestsGeneration++;
  myBestsCache = null;
  myBestsInflight = null;
}

export async function fetchMyBests(): Promise<MyBestsMap> {
  if (myBestsCache && Date.now() - myBestsCache.at < MY_BESTS_TTL_MS) return myBestsCache.data;
  if (myBestsInflight) return myBestsInflight;
  const gen = myBestsGeneration;
  const p: Promise<MyBestsMap> = (async (): Promise<MyBestsMap> => {
    try {
      const r = await fetch(apiPath("/api/my-bests"), {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!r.ok) return {};
      const data = (await r.json()) as { bests?: MyBestsMap };
      const bests = data.bests ?? {};
      if (gen === myBestsGeneration) myBestsCache = { at: Date.now(), data: bests };
      return bests;
    } catch {
      return {};
    }
  })();
  myBestsInflight = p;
  void p.finally(() => {
    if (myBestsInflight === p) myBestsInflight = null;
  });
  return p;
}

async function shopAction(
  action: string,
  cosmeticId: string,
  opts?: { customColor?: string | null; themeChannels?: ThemeCosmeticEquipSlot[] },
): Promise<{
  ok: boolean;
  dots?: ShopDotsState;
  equippedCosmeticIds?: string[];
  equippedCosmeticId?: string | null;
  equippedThemeChannels?: Partial<Record<ThemeCosmeticEquipSlot, string>>;
  alreadyOwned?: boolean;
  rebought?: boolean;
  customColor?: string | null;
  ownedCustomColors?: string[];
  activeCustomColor?: string | null;
}> {
  const r = await fetch(apiPath("/api/shop"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({
      action,
      cosmeticId,
      ...(opts && "customColor" in opts ? { customColor: opts.customColor } : {}),
      ...(opts?.themeChannels ? { themeChannels: opts.themeChannels } : {}),
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `shop ${r.status}`);
  return data as {
    ok: boolean;
    dots?: ShopDotsState;
    equippedCosmeticIds?: string[];
    equippedCosmeticId?: string | null;
    equippedThemeChannels?: Partial<Record<ThemeCosmeticEquipSlot, string>>;
    alreadyOwned?: boolean;
    rebought?: boolean;
    customColor?: string | null;
    ownedCustomColors?: string[];
    activeCustomColor?: string | null;
  };
}

async function shopCrateAction(
  action: "buy_crate" | "claim_crate" | "claim_free_crate",
  crateId: string,
): Promise<{
  ok: boolean;
  dots?: ShopDotsState;
  freeCrateRolls?: Record<string, number>;
  crateResult?: {
    crateId: string;
    crateName: string;
    rewardCosmetic: ShopCosmeticDto;
    alreadyOwned: boolean;
    duplicateRewardDots: number;
    usedFreeRoll?: boolean;
  };
}> {
  const r = await fetch(apiPath("/api/shop"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action, crateId }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `shop crate ${r.status}`);
  return data;
}

export function shopBuyCosmetic(cosmeticId: string, opts?: { customColor?: string | null }) {
  return shopAction("buy", cosmeticId, opts);
}

export function shopClaimCosmetic(cosmeticId: string, opts?: { customColor?: string | null }) {
  return shopAction("claim", cosmeticId, opts);
}

export async function shopAddCosmeticColor(
  cosmeticId: string,
  customColor: string | null,
): Promise<{
  ok: boolean;
  ownedCustomColors?: string[];
  activeCustomColor?: string | null;
  customColor?: string | null;
  dots?: ShopDotsState;
}> {
  const r = await fetch(apiPath("/api/shop"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "add_cosmetic_color", cosmeticId, customColor }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `shop color ${r.status}`);
  return data;
}

export function shopBuyCrate(crateId: string) {
  return shopCrateAction("buy_crate", crateId);
}

export function shopClaimCrate(crateId: string) {
  return shopCrateAction("claim_crate", crateId);
}

export function shopClaimFreeCrate(crateId: string) {
  return shopCrateAction("claim_free_crate", crateId);
}

export async function shopGiftCosmetic(
  cosmeticId: string,
  toUserId: string,
  opts?: { customColor?: string | null },
): Promise<{
  ok: boolean;
  gifted?: boolean;
  toUserId?: string;
  toDisplayName?: string | null;
  dots?: ShopDotsState;
}> {
  const r = await fetch(apiPath("/api/shop"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({
      action: "gift",
      cosmeticId,
      toUserId,
      customColor: opts?.customColor ?? undefined,
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `shop gift ${r.status}`);
  return data as {
    ok: boolean;
    gifted?: boolean;
    toUserId?: string;
    toDisplayName?: string | null;
    dots?: ShopDotsState;
  };
}

export async function searchUsersByName(query: string): Promise<Array<{ id: string; displayName: string | null }>> {
  const q = query.trim();
  if (q.length < 2) return [];
  const r = await fetch(`${apiPath("/api/profile")}?search=${encodeURIComponent(q)}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`profile search ${r.status}`);
  const data = (await r.json()) as { users?: Array<{ id: string; displayName: string | null }> };
  return data.users ?? [];
}

export type UserNotificationDto = {
  id: string;
  kind: string;
  title: string;
  body: string;
  data?: unknown;
  readAt: string | null;
  createdAt: string;
};

export async function fetchNotifications(limit = 50): Promise<{
  items: UserNotificationDto[];
  unreadCount: number;
}> {
  const r = await fetch(`${apiPath("/api/notifications")}?limit=${Math.min(100, Math.max(1, limit))}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 401) return { items: [], unreadCount: 0 };
  if (!r.ok) throw new Error(`notifications ${r.status}`);
  const data = (await r.json()) as { items?: UserNotificationDto[]; unreadCount?: number };
  return { items: data.items ?? [], unreadCount: data.unreadCount ?? 0 };
}

export async function markNotificationRead(id: string): Promise<void> {
  const r = await fetch(apiPath("/api/notifications"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "mark_read", id }),
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.error || `notifications ${r.status}`);
  }
}

export async function markAllNotificationsRead(): Promise<void> {
  const r = await fetch(apiPath("/api/notifications"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "mark_all_read" }),
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.error || `notifications ${r.status}`);
  }
}

export type PushPrefsDto = {
  enabled: boolean;
  friends: boolean;
  messages: boolean;
  moderation: boolean;
  shop: boolean;
  events: boolean;
  crates: boolean;
  playStreak: boolean;
  dailyReward: boolean;
  announcements: boolean;
};

export async function fetchPushVapid(): Promise<{ configured: boolean; publicKey: string | null }> {
  const r = await fetch(apiPath("/api/push-vapid"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) return { configured: false, publicKey: null };
  const data = (await r.json()) as { configured?: boolean; publicKey?: string | null };
  return {
    configured: !!data.configured,
    publicKey: data.publicKey ? String(data.publicKey) : null,
  };
}

export async function fetchPushPrefs(): Promise<{
  prefs: PushPrefsDto;
  subscriptionCount: number;
}> {
  const r = await fetch(apiPath("/api/push-prefs"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 401) {
    return {
      prefs: {
        enabled: false,
        friends: true,
        messages: true,
        moderation: true,
        shop: true,
        events: true,
        crates: true,
        playStreak: true,
        dailyReward: true,
        announcements: true,
      },
      subscriptionCount: 0,
    };
  }
  if (!r.ok) throw new Error(`push-prefs ${r.status}`);
  const data = (await r.json()) as { prefs?: PushPrefsDto; subscriptionCount?: number };
  return {
    prefs: data.prefs ?? {
      enabled: false,
      friends: true,
      messages: true,
      moderation: true,
      shop: true,
      events: true,
      crates: true,
      playStreak: true,
      dailyReward: true,
      announcements: true,
    },
    subscriptionCount: data.subscriptionCount ?? 0,
  };
}

export async function patchPushPrefs(
  patch: Partial<PushPrefsDto>,
): Promise<{ prefs: PushPrefsDto; subscriptionCount: number }> {
  const r = await fetch(apiPath("/api/push-prefs"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(patch),
  });
  const data = (await r.json().catch(() => ({}))) as {
    prefs?: PushPrefsDto;
    subscriptionCount?: number;
    error?: string;
  };
  if (!r.ok) throw new Error(data.error || `push-prefs ${r.status}`);
  return {
    prefs: data.prefs!,
    subscriptionCount: data.subscriptionCount ?? 0,
  };
}

export async function registerPushSubscription(subscription: {
  endpoint?: string;
  keys?: { p256dh?: string; auth?: string };
}): Promise<void> {
  const r = await fetch(apiPath("/api/push-subscribe"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ subscription }),
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || `push-subscribe ${r.status}`);
  }
}

export async function deletePushSubscription(endpoint?: string): Promise<void> {
  const r = await fetch(apiPath("/api/push-subscribe"), {
    method: "DELETE",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(endpoint ? { endpoint } : {}),
  });
  if (!r.ok && r.status !== 401) {
    const data = await r.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || `push-subscribe ${r.status}`);
  }
}

export function shopEquipCosmetic(
  cosmeticId: string,
  action: "equip" | "unequip" = "equip",
  opts?: { customColor?: string | null; themeChannels?: ThemeCosmeticEquipSlot[] },
) {
  return shopAction(action, cosmeticId, opts);
}

export async function shopSetEquippedCosmetics(equippedCosmeticIds: string[]): Promise<{
  ok: boolean;
  equippedCosmeticIds?: string[];
  outfits?: CosmeticOutfitDto[];
  dots?: ShopDotsState;
}> {
  const r = await fetch(apiPath("/api/shop"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "set_equipped", equippedCosmeticIds }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `shop set_equipped ${r.status}`);
  return data;
}

export async function shopSaveOutfit(opts?: {
  name?: string;
  equippedCosmeticIds?: string[];
  cursorColor?: string;
  trailColor?: string;
}): Promise<{
  ok: boolean;
  outfit?: CosmeticOutfitDto;
  outfits?: CosmeticOutfitDto[];
  dots?: ShopDotsState;
}> {
  const r = await fetch(apiPath("/api/shop"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({
      action: "save_outfit",
      name: opts?.name ?? "",
      equippedCosmeticIds: opts?.equippedCosmeticIds,
      cursorColor: opts?.cursorColor,
      trailColor: opts?.trailColor,
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `shop save_outfit ${r.status}`);
  return data;
}

export async function shopDeleteOutfit(outfitId: string): Promise<{
  ok: boolean;
  outfits?: CosmeticOutfitDto[];
  dots?: ShopDotsState;
}> {
  const r = await fetch(apiPath("/api/shop"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "delete_outfit", outfitId }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `shop delete_outfit ${r.status}`);
  return data;
}

export type PlayStreaksDto = {
  home: { streak: number; lastPlayDate: string | null };
  modes: Record<string, { streak: number; lastPlayDate: string | null }>;
};

export type PlayStreaksSyncMeta = {
  playStreaks?: PlayStreaksDto;
  today?: string;
  timeZone?: string;
  serverNow?: string;
  dayEndsAt?: string;
};

export async function postPlayStreakDay(
  modeId: string,
  timeZone?: string,
): Promise<(PlayStreaksSyncMeta & { ok: boolean; changed?: boolean }) | null> {
  const r = await fetch(apiPath("/api/play-streaks"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ modeId, ...(timeZone ? { timeZone } : {}) }),
  });
  if (r.status === 401) return null;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `play-streaks ${r.status}`);
  return data as PlayStreaksSyncMeta & { ok: boolean; changed?: boolean };
}

export async function fetchPlayStreaks(
  timeZone?: string,
): Promise<(PlayStreaksDto & PlayStreaksSyncMeta) | null> {
  const q = timeZone ? `?timeZone=${encodeURIComponent(timeZone)}` : "";
  const r = await fetch(apiPath(`/api/play-streaks${q}`), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`play-streaks ${r.status}`);
  const data = (await r.json()) as PlayStreaksSyncMeta & { playStreaks?: PlayStreaksDto };
  if (!data.playStreaks) return null;
  return { ...data.playStreaks, ...data, playStreaks: data.playStreaks };
}

export type DotsRunRewardDto = {
  dotsFromScore: number;
  bonusDots: number;
  totalDots: number;
  isNewRecord: boolean;
  isFirstRun: boolean;
};

export async function postDotsRunReward(body: {
  modeId: string;
  score: number;
  previousRecord: number;
  isNewRecord: boolean;
}): Promise<{ ok: boolean; reward?: DotsRunRewardDto; dots?: ShopDotsState } | null> {
  const r = await fetch(apiPath("/api/dots"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "run-reward", ...body }),
  });
  if (r.status === 401) return null;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `dots ${r.status}`);
  return data as { ok: boolean; reward?: DotsRunRewardDto; dots?: ShopDotsState };
}

export async function postDotsTransfer(body: {
  toUserId: string;
  amount: number;
}): Promise<{
  ok: boolean;
  amount: number;
  toUserId: string;
  toDisplayName?: string | null;
  dots?: ShopDotsState;
}> {
  const r = await fetch(apiPath("/api/dots"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "transfer", toUserId: body.toUserId, amount: body.amount }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const code = String(data.error || `dots ${r.status}`);
    const err = new Error(code);
    (err as Error & { dots?: ShopDotsState }).dots = data.dots;
    throw err;
  }
  return data as {
    ok: boolean;
    amount: number;
    toUserId: string;
    toDisplayName?: string | null;
    dots?: ShopDotsState;
  };
}

/** Persist a LootDrop spend/credit confirmed by the WebSocket room server. */
export async function postLootDropDotsAdjust(
  delta: number,
): Promise<{ ok: boolean; amount: number; dots?: ShopDotsState } | null> {
  const amount = Math.abs(Math.floor(Number(delta) || 0));
  if (amount < 1) return null;
  const action = delta < 0 ? "lootdrop-spend" : "lootdrop-credit";
  const r = await fetch(apiPath("/api/dots"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action, amount }),
  });
  if (r.status === 401) return null;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const code = String(data.error || `dots ${r.status}`);
    const err = new Error(code);
    (err as Error & { dots?: ShopDotsState }).dots = data.dots;
    throw err;
  }
  return data as { ok: boolean; amount: number; dots?: ShopDotsState };
}

/** Snap account Dots to the LootDrop room ledger balance (session end reconcile). */
export async function postLootDropDotsSync(
  balance: number,
): Promise<{ ok: boolean; balance: number; delta: number; dots?: ShopDotsState } | null> {
  const target = Math.max(0, Math.floor(Number(balance) || 0));
  const r = await fetch(apiPath("/api/dots"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "lootdrop-sync", balance: target }),
  });
  if (r.status === 401) return null;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const code = String(data.error || `dots ${r.status}`);
    const err = new Error(code);
    (err as Error & { dots?: ShopDotsState }).dots = data.dots;
    throw err;
  }
  return data as { ok: boolean; balance: number; delta: number; dots?: ShopDotsState };
}

/** Persist a shop cosmetic awarded by catching a LootDrop item drop. */
export async function postLootDropGrantCosmetic(cosmeticId: string): Promise<{
  ok: boolean;
  alreadyOwned?: boolean;
  dots?: ShopDotsState;
} | null> {
  const id = String(cosmeticId || "").trim();
  if (!id) return null;
  const r = await fetch(apiPath("/api/shop"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "lootdrop_grant", cosmeticId: id }),
  });
  if (r.status === 401) return null;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `shop lootdrop_grant ${r.status}`);
  return data as { ok: boolean; alreadyOwned?: boolean; dots?: ShopDotsState };
}

export type AdminShopDraftSummary = {
  id: string;
  name: string | null;
  authorName: string | null;
  updatedAt: string;
};

export type AdminShopCrateRow = ShopCrateDto & {
  isPublished: boolean;
  isConfigured: boolean;
  createdAt: string;
  updatedAt: string;
};

export async function fetchAdminShopDrafts(): Promise<AdminShopDraftSummary[]> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 12_000);
  try {
    const r = await fetch(apiPath("/api/admin-shop-drafts"), {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (r.status === 403) throw new Error("forbidden");
    if (!r.ok) throw new Error(`admin shop drafts ${r.status}`);
    const data = (await r.json()) as { items: AdminShopDraftSummary[] };
    return data.items ?? [];
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") throw new Error("Shop drafts request timed out");
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

export async function fetchAdminShopDraft(id: string): Promise<{ id: string; name: string | null; data: unknown }> {
  const r = await fetch(`${apiPath("/api/admin-shop-drafts")}?${new URLSearchParams({ id })}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`admin shop draft ${r.status}`);
  const data = (await r.json()) as { item: { id: string; name: string | null; data: unknown } };
  return data.item;
}

export async function saveAdminShopDraft(payload: { name?: string; data: unknown }): Promise<void> {
  const r = await fetch(apiPath("/api/admin-shop-drafts"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`save shop draft ${r.status}`);
}

export async function deleteAdminShopDraft(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/admin-shop-drafts")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete shop draft ${r.status}`);
}

export async function fetchAdminShopCrateDrafts(): Promise<AdminShopDraftSummary[]> {
  const r = await fetch(apiPath("/api/admin-shop-crate-drafts"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin crate drafts ${r.status}`);
  const data = (await r.json()) as { items?: AdminShopDraftSummary[] };
  return data.items ?? [];
}

export async function fetchAdminShopCrateDraft(id: string): Promise<{ id: string; name: string | null; data: unknown }> {
  const r = await fetch(`${apiPath("/api/admin-shop-crate-drafts")}?${new URLSearchParams({ id })}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`admin crate draft ${r.status}`);
  const data = (await r.json()) as { item: { id: string; name: string | null; data: unknown } };
  return data.item;
}

export async function saveAdminShopCrateDraft(payload: { name?: string; data: unknown }): Promise<void> {
  const r = await fetch(apiPath("/api/admin-shop-crate-drafts"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`save crate draft ${r.status}`);
}

export async function deleteAdminShopCrateDraft(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/admin-shop-crate-drafts")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete crate draft ${r.status}`);
}

export type AdminShopCosmeticRow = ShopCosmeticDto & {
  createdAt: string;
  updatedAt: string;
  unlockLabel: string;
};

export async function fetchAdminShopCosmetics(): Promise<AdminShopCosmeticRow[]> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 12_000);
  try {
    const r = await fetch(apiPath("/api/admin-shop-cosmetics"), {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (r.status === 403) throw new Error("forbidden");
    if (!r.ok) throw new Error(`admin shop cosmetics ${r.status}`);
    const data = (await r.json()) as { items: AdminShopCosmeticRow[] };
    return data.items ?? [];
  } catch (e) {
    if (e instanceof Error && e.name === "AbortError") throw new Error("Shop items request timed out");
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

export async function publishAdminShopCosmetic(payload: {
  name: string;
  imageData: string;
  outlineMaskData?: string | null;
  animationFrames?: string[] | null;
  category?: CosmeticCategory;
  trailMode?: TrailMode | null;
  trailColors?: string[] | null;
  themeData?: ThemeCosmeticData | null;
  allowCustomColor?: boolean;
  allowRepurchase?: boolean;
  matchCursorColor?: boolean;
  cursorColorShade?: number;
  matchCursorMaskData?: string | null;
  matchCursorMaskFrames?: string[] | null;
  unlockType: ShopUnlockType;
  priceDots?: number;
  unlockAchievementId?: string;
  unlockChallengeId?: string;
  unlockScoreMode?: string;
  unlockScoreValue?: number;
  crateOnly?: boolean;
  sentOnly?: boolean;
  sendToUserIds?: string[];
  limitedItem?: boolean;
  limitedDurationText?: string | null;
  limitedDuration?: string | null;
  newCursor?: boolean;
}): Promise<{ id: string; sent?: { granted: number; recipients: number } }> {
  const r = await fetch(apiPath("/api/admin-shop-cosmetics"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(
      (data as { error?: string }).error === "send_recipients_required"
        ? "Add at least one recipient."
        : `publish shop cosmetic ${r.status}`,
    );
  }
  const data = (await r.json()) as {
    item?: { id?: string };
    sent?: { granted: number; recipients: number };
  };
  return { id: data.item?.id ?? "", sent: data.sent };
}

export async function patchAdminShopCosmetic(payload: {
  id: string;
  name?: string;
  imageData?: string;
  outlineMaskData?: string | null;
  animationFrames?: string[] | null;
  unlockType?: ShopUnlockType;
  priceDots?: number;
  unlockAchievementId?: string;
  unlockChallengeId?: string;
  unlockScoreMode?: string;
  unlockScoreValue?: number;
  category?: CosmeticCategory;
  trailMode?: TrailMode | null;
  trailColors?: string[] | null;
  themeData?: ThemeCosmeticData | null;
  allowCustomColor?: boolean;
  allowRepurchase?: boolean;
  matchCursorColor?: boolean;
  cursorColorShade?: number;
  matchCursorMaskData?: string | null;
  matchCursorMaskFrames?: string[] | null;
  crateOnly?: boolean;
  sentOnly?: boolean;
  sendToUserIds?: string[];
  revokeOwners?: boolean;
  notifyShop?: boolean;
  limitedItem?: boolean;
  limitedDurationText?: string | null;
  limitedDuration?: string | null;
  newCursor?: boolean;
}): Promise<{ revoked?: number; sent?: { granted: number; recipients: number } }> {
  const r = await fetch(apiPath("/api/admin-shop-cosmetics"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`patch shop cosmetic ${r.status}`);
  const data = (await r.json().catch(() => ({}))) as {
    revoked?: number;
    sent?: { granted: number; recipients: number };
  };
  return { revoked: data.revoked, sent: data.sent };
}

export async function deleteAdminShopCosmetic(id: string): Promise<{ refundsProcessed?: number }> {
  const r = await fetch(`${apiPath("/api/admin-shop-cosmetics")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete shop cosmetic ${r.status}`);
  return r.json() as Promise<{ refundsProcessed?: number }>;
}

export async function fetchAdminShopCrates(): Promise<AdminShopCrateRow[]> {
  const r = await fetch(apiPath("/api/admin-shop-crates"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin shop crates ${r.status}`);
  const data = (await r.json()) as { items?: AdminShopCrateRow[] };
  return data.items ?? [];
}

export async function publishAdminShopCrate(payload: {
  name?: string;
  imageData: string;
}): Promise<{ id: string }> {
  const r = await fetch(apiPath("/api/admin-shop-crates"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`publish shop crate ${r.status}`);
  const data = (await r.json()) as { item?: { id?: string } };
  return { id: data.item?.id ?? "" };
}

export async function patchAdminShopCrate(payload: {
  id: string;
  name?: string;
  imageData?: string;
  unlockType?: ShopUnlockType;
  priceDots?: number | null;
  unlockAchievementId?: string | null;
  unlockChallengeId?: string | null;
  unlockScoreMode?: string | null;
  unlockScoreValue?: number | null;
  sortOrder?: number;
  isPublished?: boolean;
  contents?: Array<{ cosmeticId: string; probabilityPercent: number }>;
}): Promise<void> {
  const r = await fetch(apiPath("/api/admin-shop-crates"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.error || `patch shop crate ${r.status}`);
  }
}

export async function deleteAdminShopCrate(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/admin-shop-crates")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete shop crate ${r.status}`);
}

export async function fetchAdminDotsConfig(): Promise<ModeDotsConfigDto[]> {
  const r = await fetch(apiPath("/api/admin-dots-config"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin dots config ${r.status}`);
  const data = (await r.json()) as { items: ModeDotsConfigDto[] };
  return data.items ?? [];
}

export async function patchAdminDotsConfig(payload: {
  modeId: string;
  dotsConversionRate?: number;
  dotsBonusBase?: number;
}): Promise<void> {
  const r = await fetch(apiPath("/api/admin-dots-config"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`patch dots config ${r.status}`);
}

export async function fetchSplashTexts(): Promise<string[]> {
  const r = await fetch(apiPath("/api/splashes"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`splashes ${r.status}`);
  const data = (await r.json()) as { texts?: string[] };
  return Array.isArray(data.texts) ? data.texts.filter((t) => typeof t === "string") : [];
}

export async function fetchAdminSplashTexts(): Promise<{ texts: string[]; updatedAt: string | null }> {
  const r = await fetch(apiPath("/api/admin-splashes"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin splashes ${r.status}`);
  const data = (await r.json()) as { texts?: string[]; updatedAt?: string | null };
  return {
    texts: Array.isArray(data.texts) ? data.texts.filter((t) => typeof t === "string") : [],
    updatedAt: data.updatedAt ?? null,
  };
}

export async function putAdminSplashTexts(texts: string[]): Promise<{ texts: string[]; updatedAt: string | null }> {
  const r = await fetch(apiPath("/api/admin-splashes"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ texts }),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`put admin splashes ${r.status}`);
  const data = (await r.json()) as { texts?: string[]; updatedAt?: string | null };
  return {
    texts: Array.isArray(data.texts) ? data.texts.filter((t) => typeof t === "string") : [],
    updatedAt: data.updatedAt ?? null,
  };
}

export type HubIconCategoryId =
  | "modes"
  | "practice"
  | "sandbox"
  | "multiplayer"
  | "local"
  | "challenges"
  | "tournaments"
  | "marathon"
  | "lootdrops"
  | "campaign"
  | "worldmap";

export type HubIconRow = {
  categoryId: HubIconCategoryId;
  maskData: string;
  updatedAt: string;
};

export async function fetchHubIcons(): Promise<Record<string, { maskData: string; updatedAt: string }>> {
  const r = await fetch(apiPath("/api/hub-icons"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`hub icons ${r.status}`);
  const data = (await r.json()) as { icons?: Record<string, { maskData: string; updatedAt: string }> };
  return data.icons ?? {};
}

export async function fetchAdminHubIcons(): Promise<HubIconRow[]> {
  const r = await fetch(apiPath("/api/admin-hub-icons"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin hub icons ${r.status}`);
  const data = (await r.json()) as { icons?: HubIconRow[] };
  return Array.isArray(data.icons) ? data.icons : [];
}

export async function publishAdminHubIcon(payload: {
  categoryId: HubIconCategoryId;
  maskData: string;
}): Promise<HubIconRow> {
  const r = await fetch(apiPath("/api/admin-hub-icons"), {
    method: "PUT",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`publish hub icon ${r.status}`);
  return r.json() as Promise<HubIconRow>;
}

export async function revertAdminHubIcon(categoryId: HubIconCategoryId): Promise<void> {
  const p = new URLSearchParams({ categoryId });
  const r = await fetch(`${apiPath("/api/admin-hub-icons")}?${p}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`revert hub icon ${r.status}`);
}

export type AdminAnnouncementRow = {
  id: string;
  title: string;
  body: string;
  recipientCount: number;
  createdById: string | null;
  createdAt: string;
};

export async function fetchAdminAnnouncements(): Promise<AdminAnnouncementRow[]> {
  const r = await fetch(apiPath("/api/admin-announcements"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin announcements ${r.status}`);
  const data = (await r.json()) as { announcements?: AdminAnnouncementRow[] };
  return Array.isArray(data.announcements) ? data.announcements : [];
}

export async function sendAdminAnnouncement(input: {
  title: string;
  body: string;
}): Promise<{ announcement: AdminAnnouncementRow; recipientCount: number }> {
  const r = await fetch(apiPath("/api/admin-announcements"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ title: input.title, body: input.body }),
  });
  if (r.status === 403) throw new Error("forbidden");
  const data = (await r.json().catch(() => ({}))) as {
    announcement?: AdminAnnouncementRow;
    recipientCount?: number;
    error?: string;
  };
  if (!r.ok) throw new Error(data.error || `admin announcements ${r.status}`);
  if (!data.announcement) throw new Error("invalid_response");
  return {
    announcement: data.announcement,
    recipientCount: data.recipientCount ?? data.announcement.recipientCount ?? 0,
  };
}

export type WhatsNewPostRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string;
};

export type AdminWhatsNewPostRow = WhatsNewPostRow & {
  active: boolean;
  createdById: string | null;
};

export async function fetchWhatsNewPosts(): Promise<WhatsNewPostRow[]> {
  const r = await fetch(apiPath("/api/whats-new"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) return [];
  const data = (await r.json().catch(() => ({}))) as { posts?: WhatsNewPostRow[] };
  return Array.isArray(data.posts) ? data.posts : [];
}

export async function fetchAdminWhatsNewPosts(): Promise<AdminWhatsNewPostRow[]> {
  const r = await fetch(apiPath("/api/admin-whats-new"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin whats-new ${r.status}`);
  const data = (await r.json()) as { posts?: AdminWhatsNewPostRow[] };
  return Array.isArray(data.posts) ? data.posts : [];
}

export async function createAdminWhatsNewPost(input: {
  title: string;
  body: string;
  active?: boolean;
}): Promise<AdminWhatsNewPostRow> {
  const r = await fetch(apiPath("/api/admin-whats-new"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(input),
  });
  if (r.status === 403) throw new Error("forbidden");
  const data = (await r.json().catch(() => ({}))) as { post?: AdminWhatsNewPostRow; error?: string };
  if (!r.ok) throw new Error(data.error || `admin whats-new ${r.status}`);
  if (!data.post) throw new Error("invalid_response");
  return data.post;
}

export async function deleteAdminWhatsNewPost(id: string): Promise<void> {
  const p = new URLSearchParams({ id });
  const r = await fetch(`${apiPath("/api/admin-whats-new")}?${p}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin whats-new delete ${r.status}`);
}

/* ── Theme Creator ─────────────────────────────────────────────────────── */

export type GameThemeData = {
  colors: {
    backgroundColor: string;
    backgroundGradient: boolean;
    backgroundGradientTo: string;
    ballsColor: string;
    ballsGradient: boolean;
    ballsGradientTo: string;
    buttonsColor: string;
    textColor: string;
    buttonsGradient: boolean;
    buttonsGradientTo: string;
    textGradient: boolean;
    textGradientTo: string;
    dontChangeBackground?: boolean;
    dontChangeBalls?: boolean;
    dontChangeButtons?: boolean;
    dontChangeText?: boolean;
  };
  decorations: Array<{
    id: string;
    kind: "static" | "animated" | "particles";
    imageData: string;
    frames?: string[];
    anchor: "viewport" | "sidebar";
    xPct: number;
    yPct: number;
    widthPct: number;
    heightPct: number;
    screens: string[];
    zIndex?: number;
    opacity?: number;
    layer?: "front" | "back";
    attachSelector?: string;
    rotationDeg?: number;
    particleConfig?: {
      spawnRate: number;
      fallSpeedMin: number;
      fallSpeedMax: number;
      driftAmplitude: number;
      driftFrequency: number;
      rotationSpeedMin: number;
      rotationSpeedMax: number;
      scaleMin: number;
      scaleMax: number;
      opacityFade: boolean;
      windAngleDeg: number;
      maxParticles: number;
      baseSizePx: number;
    };
  }>;
  music?: {
    enabled: boolean;
    bpm: number;
    bars: number;
    oscillator: "sine" | "square" | "sawtooth" | "triangle";
    scope: "menu" | "game" | "both";
    cells: Array<{ note: number; step: number }>;
  };
};

export type AdminThemeDraftSummary = {
  id: string;
  name: string | null;
  authorName: string | null;
  updatedAt: string;
};

export type AdminThemeRow = {
  id: string;
  name: string;
  data: GameThemeData;
  isActive: boolean;
  scheduleStart: string | null;
  scheduleEnd: string | null;
  authorName: string | null;
  createdAt: string;
  updatedAt: string;
  liveNow?: boolean;
};

export async function fetchActiveGameTheme(): Promise<{
  id: string;
  name: string;
  data: GameThemeData;
} | null> {
  const r = await fetch(apiPath("/api/game-theme"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) return null;
  const data = (await r.json()) as {
    theme?: { id: string; name: string; data: GameThemeData } | null;
  };
  return data.theme ?? null;
}

export async function fetchAdminThemeDrafts(): Promise<AdminThemeDraftSummary[]> {
  const r = await fetch(apiPath("/api/admin-theme-drafts"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin theme drafts ${r.status}`);
  const data = (await r.json()) as { items?: AdminThemeDraftSummary[] };
  return data.items ?? [];
}

export async function fetchAdminThemeDraft(
  id: string,
): Promise<{ id: string; name: string | null; data: GameThemeData }> {
  const r = await fetch(`${apiPath("/api/admin-theme-drafts")}?${new URLSearchParams({ id })}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin theme draft ${r.status}`);
  const data = (await r.json()) as {
    item?: { id: string; name: string | null; data: GameThemeData };
  };
  if (!data.item) throw new Error("not_found");
  return data.item;
}

export async function saveAdminThemeDraft(payload: {
  id?: string;
  name?: string;
  data: unknown;
}): Promise<{ id: string; name: string | null; data: GameThemeData }> {
  const r = await fetch(apiPath("/api/admin-theme-drafts"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`save theme draft ${r.status}`);
  const data = (await r.json()) as {
    item?: { id: string; name: string | null; data: GameThemeData };
  };
  if (!data.item) throw new Error("invalid_response");
  return data.item;
}

export async function deleteAdminThemeDraft(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/admin-theme-drafts")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete theme draft ${r.status}`);
}

export async function fetchAdminThemes(): Promise<{
  items: AdminThemeRow[];
  activeId: string | null;
}> {
  const r = await fetch(apiPath("/api/admin-themes"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin themes ${r.status}`);
  const data = (await r.json()) as { items?: AdminThemeRow[]; activeId?: string | null };
  return { items: data.items ?? [], activeId: data.activeId ?? null };
}

export async function fetchAdminTheme(id: string): Promise<AdminThemeRow> {
  const r = await fetch(`${apiPath("/api/admin-themes")}?${new URLSearchParams({ id })}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin theme ${r.status}`);
  const data = (await r.json()) as { item?: AdminThemeRow };
  if (!data.item) throw new Error("not_found");
  return data.item;
}

export async function saveAdminTheme(payload: {
  name: string;
  data: unknown;
  scheduleStart?: string | null;
  scheduleEnd?: string | null;
}): Promise<AdminThemeRow> {
  const r = await fetch(apiPath("/api/admin-themes"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`save theme ${r.status}`);
  const data = (await r.json()) as { item?: AdminThemeRow };
  if (!data.item) throw new Error("invalid_response");
  return data.item;
}

export async function patchAdminTheme(payload: {
  id: string;
  action?: "update" | "activate" | "deactivate";
  name?: string;
  data?: unknown;
  scheduleStart?: string | null;
  scheduleEnd?: string | null;
}): Promise<AdminThemeRow | { ok: true; activeId: null }> {
  const r = await fetch(apiPath("/api/admin-themes"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`patch theme ${r.status}`);
  const data = (await r.json()) as {
    item?: AdminThemeRow;
    ok?: boolean;
    activeId?: null;
  };
  if (data.ok && data.activeId === null) return { ok: true, activeId: null };
  if (!data.item) throw new Error("invalid_response");
  return data.item;
}

export async function deleteAdminTheme(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/admin-themes")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`delete theme ${r.status}`);
}

/* ─── Replays ─── */

export type ReplayMeta = {
  id?: string;
  userId?: string;
  mode?: string;
  metric?: string;
  value?: number;
  gameVersion?: string | null;
  durationMs?: number;
  device?: string | null;
  at?: string;
  available: boolean;
  reason?: "none" | "incompatible" | "missing";
  payload?: unknown;
};

export async function fetchReplayMeta(opts: {
  userId?: string;
  mode: string;
  metric?: string;
  level?: number;
  presetId?: string;
  mine?: boolean;
  play?: boolean;
}): Promise<ReplayMeta> {
  const q = new URLSearchParams({ mode: opts.mode });
  if (opts.userId) q.set("userId", opts.userId);
  if (opts.metric) q.set("metric", opts.metric);
  if (opts.level != null) q.set("level", String(opts.level));
  if (opts.presetId) q.set("presetId", opts.presetId);
  if (opts.mine) q.set("mine", "1");
  if (opts.play) q.set("play", "1");
  const r = await fetch(`${apiPath("/api/replay")}?${q}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) return { available: false, reason: "none" };
  return (await r.json()) as ReplayMeta;
}

export async function fetchUserReplayMap(userId: string): Promise<Record<string, ReplayMeta>> {
  const q = new URLSearchParams({ modes: "1", userId });
  const r = await fetch(`${apiPath("/api/replay")}?${q}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) return {};
  const data = (await r.json()) as { replays?: Record<string, ReplayMeta> };
  return data.replays ?? {};
}

export async function submitModeReplay(payload: {
  mode: string;
  value: number;
  replay: unknown;
  level?: number;
  presetId?: string;
  clientVersion?: string;
  device?: "pc" | "mobile";
}): Promise<{ ok: boolean; saved?: boolean; id?: string; error?: string }> {
  const r = await fetch(apiPath("/api/replay"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
  const data = (await r.json().catch(() => ({}))) as {
    ok?: boolean;
    saved?: boolean;
    id?: string;
    error?: string;
  };
  if (!r.ok) return { ok: false, error: data.error || `replay ${r.status}` };
  return { ok: true, saved: !!data.saved, id: data.id };
}

export type TournamentSummary = {
  id: string;
  name: string;
  bracketSize: number;
  status: string;
  startsAt: string;
  registeredCount: number;
  registered: boolean;
  winnerUserId: string | null;
  winnerName: string | null;
};

export type TournamentListResponse = {
  upcoming: TournamentSummary[];
  active: TournamentSummary[];
  ended: TournamentSummary[];
  me?: {
    registered: TournamentSummary[];
    dotsEarned: number;
    tournamentsWon: number;
  };
  serverNow: string;
};

export async function fetchTournaments(): Promise<TournamentListResponse> {
  const r = await fetch(apiPath("/api/tournaments"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`tournaments ${r.status}`);
  return (await r.json()) as TournamentListResponse;
}

export async function fetchTournamentDetail(id: string): Promise<Record<string, unknown>> {
  const r = await fetch(`${apiPath("/api/tournaments")}?id=${encodeURIComponent(id)}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`tournament ${r.status}`);
  return (await r.json()) as Record<string, unknown>;
}

export async function postTournament(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const r = await fetch(apiPath("/api/tournaments"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new Error(String(data.error || `tournaments ${r.status}`));
  return data;
}

export async function fetchAdminTournaments(status?: string): Promise<Record<string, unknown>> {
  const p = new URLSearchParams();
  if (status) p.set("status", status);
  const r = await fetch(`${apiPath("/api/admin-tournaments")}?${p}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin tournaments ${r.status}`);
  return (await r.json()) as Record<string, unknown>;
}

export async function fetchAdminTournamentTemplates(): Promise<Record<string, unknown>> {
  const r = await fetch(`${apiPath("/api/admin-tournaments")}?section=templates`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin tournament templates ${r.status}`);
  return (await r.json()) as Record<string, unknown>;
}

export async function postAdminTournament(body: Record<string, unknown>): Promise<void> {
  const r = await fetch(apiPath("/api/admin-tournaments"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || `admin tournaments ${r.status}`);
  }
}

export async function patchAdminTournament(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const r = await fetch(apiPath("/api/admin-tournaments"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  if (r.status === 403) throw new Error("forbidden");
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new Error(String(data.error || `admin tournaments ${r.status}`));
  return data;
}

export async function deleteAdminTournament(id: string, isTemplate = false): Promise<void> {
  const p = new URLSearchParams({ id });
  if (isTemplate) p.set("isTemplate", "1");
  const r = await fetch(`${apiPath("/api/admin-tournaments")}?${p}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin tournaments delete ${r.status}`);
}
